import React, { useState } from "react";

import {
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  IconButton,
  Chip,
  Button,
  Tooltip,
} from "@mui/material";
import { addressToString, getJobTypeName, getUserPhotoSrc } from "../utils/utils";
import {
  AccessTime,
  AccessTimeFilled,
  Apartment,
  CheckCircle,
  CleaningServices,
  Close,
  Construction,
  ContentCopy,
  Error,
  Grass,
  House,
  Warning,
} from "@mui/icons-material";

import constants from "../static/constants.json";
import { Edit } from "@mui/icons-material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { editableStatuses } from "../static/static";
type JobListItemProps = {
  job: any;
  onSelect: () => void;
  onEdit?: () => void;
  onDelete: () => void;
  onDuplicate?: () => void;
  onView?: () => void;
  onComplete?: () => void;
  onCancel?: () => void;
  onStart?: () => void;
  requested?: boolean;
};

const JobListItem = ({
  job,
  onSelect,
  onDelete,
  onEdit,
  onView,
  onDuplicate,
  onComplete,
  onStart,
  onCancel,
  requested,
}: JobListItemProps) => {
  const user = useSelector((state: any) => state.user);
  const [hovered, setHovered] = useState(false);
  const getIconContent = () => {
    switch (job.typeId) {
      case constants.jobType.cleaning:
        return <CleaningServices />;
      case constants.jobType.landscaping:
        return <Grass />;
      case constants.jobType.maintenance:
        return <Construction />;

      default:
        <Error />;
    }
    if (job.type.id === constants.propertyType.house) return <House />;
    else return <Apartment />;
  };


  const getStartButton = () => {
    if (
      user.id === job.assigneeId &&
      job.statusId === constants.jobStatus.assigned
    ) {
      return (
        <Button
          variant="outlined"
          startIcon={<CheckCircle />}
          onClick={onStart}
        >
          Start
        </Button>
      );
    } else return <></>;
  };

  const getCompleteButton = () => {
    if (
      user.id === job.assigneeId &&
      job.statusId === constants.jobStatus.started
    ) {
      return (
        <Button
          variant="outlined"
          startIcon={<CheckCircle />}
          onClick={onComplete}
        >
          Complete
        </Button>
      );
    } else return <></>;
  };

  const getEditButton = () => {
    if (
      user.personTypeId === constants.personType.propertyManager &&
      editableStatuses.indexOf(job.statusId) > -1 &&
      dayjs(job.endTime).isAfter(dayjs())
    ) {
      return (
        <Tooltip title="Edit job">
          <IconButton onClick={onEdit} color="primary">
          <Edit />
        </IconButton>
        </Tooltip>
        
      );
    } else return <></>;
  };

  const getDeleteButton = () => {
    if (
      user.personTypeId === constants.personType.propertyManager &&
      editableStatuses.indexOf(job.statusId) > -1 &&
      dayjs(job.endTime).isAfter(dayjs())
    ) {
      return (
        <Tooltip title="Cancel job">
          <IconButton onClick={onDelete} color="error">
          <Close />
        </IconButton>
        </Tooltip>
        
      );
    } else return <></>;
  };

  const getDuplicateButton = () => {
    if (
      user.personTypeId === constants.personType.propertyManager
    ) {
      return (
        <Tooltip title="Duplicate job">
          <IconButton onClick={onDuplicate} color="primary">
          <ContentCopy />
        </IconButton>
        </Tooltip>
        
      );
    } else return <></>;
  };

  const getFee = () => {
    if (
      true
    ) {
      return (
        <Tooltip title="Completion fee">
          <IconButton style={{cursor: "default"}}onClick={() => {}} color="success">
          {`$${job.fee}`}
        </IconButton>
        </Tooltip>
        
      );
    } else return <></>;
  };

  const getRequested = () => {
    if (
      requested
    ) {
      return (
        <Tooltip title="Requested.  Waiting for decision.">
          <AccessTime color="warning" />
        </Tooltip>
        
      );
    } else return <></>;
  };

  const getBeingVerified = () => {
    if (
      job.statusId === constants.jobStatus.completed
    ) {
      return (
        <Tooltip title="Verification pending">
          <AccessTimeFilled color="warning" />
        </Tooltip>
        
      );
    } else return <></>;
  };

  const getWasApproved = () => {
    if (
      job.statusId === constants.jobStatus.accepted
    ) {
      return (
        <Tooltip title="Accepted">
          <CheckCircle color="success" />
        </Tooltip>
        
      );
    } else return <></>;
  };

  const getWasRejected = () => {
    if (
      job.statusId === constants.jobStatus.rejected
    ) {
      return (
        <Tooltip title="Rejected">
          <Error color="error" />
        </Tooltip>
        
      );
    } else return <></>;
  };

  const getActions = () => {
    return (
      <div style={{display: 'flex', alignItems: 'center'}}>
        {getDeleteButton()}
        {getEditButton()}
        {getDuplicateButton()}
        {getCompleteButton()}
        {getStartButton()}
        {getRequested()}
        {getBeingVerified()}
        {getWasApproved()}
        {getWasRejected()}
        {getFee()}
        
      </div>
    );
  };

  const getTimeString = (startTime: Date, endTime: Date) => {
    if (dayjs(startTime).format("LL") === dayjs(endTime).format("LL")) {
      return `between ${dayjs(startTime).format("LT")} and ${dayjs(
        endTime
      ).format("LT")} on ${dayjs(startTime).format("MMM D")}`;
    } else {
      return `between ${dayjs(startTime).format("LT")} on ${dayjs(
        startTime
      ).format("MMM D")} and  ${dayjs(endTime).format("LT")} on ${dayjs(
        endTime
      ).format("MMM D")}`;
    }
  };
  const getChipContent = () => {
    const now = new Date().getTime();
    const twoHoursFromNow = new Date(now + 2 * 60 * 60 * 1000).getTime();
    const end = new Date(job.endTime).getTime();
    const expiresChip =
      now < end && twoHoursFromNow > end ? (
        <Chip
          color="warning"
          variant="outlined"
          icon={<Warning sx={{ height: "12px", width: "12px" }} />}
          label="Ending soon"
        />
      ) : null;
    return (
      <>
        <br />
        <span>{expiresChip}</span>
      </>
    );
  };

  const getAvatar = () => {
    if (user.personTypeId === constants.personType.propertyManager && job.assigneeId !== null) {
      return <Avatar src={getUserPhotoSrc(job.assigneeId)}></Avatar>
    } else {
      return <Avatar>{getIconContent()}</Avatar>
    }
  };

  return (
    <ListItem
      alignItems="flex-start"
      style={{
        border: "1px none green",
        cursor: "pointer",
        background: hovered ? "#eaeaea" : "#ffffff",
      }}
      secondaryAction={<>{getActions()}</>}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ListItemAvatar>
        {getAvatar()}
        
      </ListItemAvatar>
      <ListItemText
        primary={`${addressToString(job.property.address)}`}
        onClick={onSelect}
        secondary={
          <React.Fragment>
            <Typography
              component="span"
              variant="body2"
              sx={{ color: "text.primary", display: "inline" }}
            >
              {getJobTypeName(job.typeId)} {getTimeString(job.startTime, job.endTime)}
            </Typography>
            {getChipContent()}
            {/* {" — I'll be in your neighborhood doing errands this…"} */}
          </React.Fragment>
        }
      />
    </ListItem>
  );
};

export default JobListItem;
